'use client';

import EyeOffIconSvg from '@/assets/svgs/eye-off.svg';
import EyeOnIconSvg from '@/assets/svgs/eye-on.svg';
import UserAddIconSvg from '@/assets/svgs/user-add-filled.svg';

import { Modals } from '@/containers/Modals/all-modals';
import { selectCurrentAccountId } from '@/lib/redux/app.selectors';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalHeader from '../ModalHeader';
import { CreateNewAgentModalParams, ModalProps } from '../types';
import useModals from '../useModals';

import { inter500 } from '@/config/fonts';
import { useCreateAgent } from '@/containers/Agents/api';
import {
  setAssignAgentAutomatically,
  setIsNewAgentCreated,
} from '@/containers/Jobs/redux/jobEditors.slice';
import CreateAgentValidationSchema from '@/containers/Modals/CreateNewAgentModal/validators';
import { useToast } from '@/containers/ToastMessage';
import { useAppDispatch } from '@/lib/redux/store';
import { InputContainer } from '@/shared/components/FormInputs';
import ModalContainer from '../ModalContainer';

interface CreateNewAgentModalProps extends ModalProps {
  className?: string;
  params?: CreateNewAgentModalParams;
}
export interface CreateAgentFormValues {
  accountNumber: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  role?: 'responder' | 'non-responder';
}

const modalName = Modals.CreateNewAgent;
const CreateNewAgentModal = ({
  className,
  params,
  variant = 'default',
}: CreateNewAgentModalProps) => {
  const dispatch = useAppDispatch();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { showError, showSuccess, showInfo } = useToast();
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Modal Specific details
  const jobId = params?.jobId || '';
  const [isPasswordHidden, setIsPasswordHidden] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentAccountId = useSelector(selectCurrentAccountId);
  const [isAssigningToJob, setIsAssigningToJob] = useState(false);

  const {
    data: createAgentData,
    error: createAgentError,
    isPending: isCreateAgentLoading,
    mutate: createAgent,
    reset: resetCreateAgentRequest,
  } = useCreateAgent();

  const formik = useFormik<CreateAgentFormValues>({
    validateOnBlur: true,
    enableReinitialize: true,
    initialValues: {
      accountNumber: currentAccountId as string,
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'responder',
    },
    validationSchema: CreateAgentValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      formik.validateForm();
      try {
        resetCreateAgentRequest();
        setSubmitting(true);
        createAgent({
          account_id: values.accountNumber,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
        });
      } catch (e) {
        setSubmitting(false);
        console.error(e);
      }
    },
  });

  const isLoading = formik.isSubmitting;

  // Agent created, show appropriate message and Assign to New Job if required
  useEffect(() => {
    if (!isCreateAgentLoading && !!createAgentError) {
      showError(
        'Sorry, there was an error in creating the Canvasser. Please try again later or try changing the email address.'
      );
      formik?.resetForm();
      resetCreateAgentRequest();
    }

    if (!isCreateAgentLoading && !!createAgentData?.data) {
      formik.setSubmitting(false);
      formik?.resetForm();
      resetCreateAgentRequest();
      if (isAssigningToJob) {
        showSuccess('Your Canvasser has been created and assigned to this job.');
        dispatch(
          setAssignAgentAutomatically({
            jobId,
            agent: {
              agent_id: createAgentData.data.id,
              isResponder: formik.values?.role === 'responder',
            },
          })
        );
      } else {
        showInfo('Your Canvasser has been created.');
      }
      dispatch(setIsNewAgentCreated({ jobId, isCreated: true }));
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createAgentData?.data,
    createAgentError,
    dispatch,
    isAssigningToJob,
    isCreateAgentLoading,
    onClose,
    showError,
    showSuccess,
    showInfo,
    formik.values,
  ]);

  const unmount = () => {
    if (formik) {
      formik?.resetForm();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}>
        <form onSubmit={formik.handleSubmit} className="h-full">
          <div className="flex h-full flex-col">
            {/* Stick to top */}
            <ModalHeader
              modalName={modalName}
              icon={<UserAddIconSvg className="h-5 w-5" />}
              title="Create New Canvasser"
            />

            {/* Take up remaining space and enable inside-scrolling */}
            <ModalBody ref={modalBodyRef} className="px-5 !py-0">
              {/* Spacing for consistency */}
              <div className="h-6" />
              {/* Form: START */}
              <div className="form py-2">
                <div
                  className={classNames('flex flex-col gap-2', {
                    'disabled-container': isLoading,
                  })}>
                  <InputContainer
                    label="First Name"
                    error={formik.errors.first_name}
                    isTouched={formik.touched.first_name}>
                    <input
                      type="text"
                      name="first_name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.first_name}
                      placeholder="John"
                      className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                    />
                  </InputContainer>
                  <InputContainer
                    label="Last Name"
                    error={formik.errors.last_name}
                    isTouched={formik.touched.last_name}>
                    <input
                      type="text"
                      name="last_name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.last_name}
                      placeholder="Doe"
                      className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                    />
                  </InputContainer>
                  <InputContainer
                    label="Email"
                    error={formik.errors.email}
                    isTouched={formik.touched.email}>
                    <input
                      type="email"
                      name="email"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.email}
                      placeholder="john.doe@campaign.com"
                      className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                    />
                  </InputContainer>

                  <InputContainer
                    label="Password"
                    error={formik.errors.password}
                    isTouched={formik.touched.password}>
                    <div className="relative w-full">
                      <input
                        type={isPasswordHidden ? 'password' : 'text'}
                        name="password"
                        autoComplete="new-password"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.password}
                        placeholder="Password"
                        className="focus:input-primary active:input-primary input input-bordered w-full pr-10 text-sm"
                      />
                      {!!formik.values.password && (
                        <button
                          type="button"
                          onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                          className="hover:text-gray-default absolute top-1/2 right-3 -translate-y-1/2 text-gray-400"
                          tabIndex={-1}>
                          {isPasswordHidden ? (
                            <EyeOffIconSvg className="h-5 w-5" />
                          ) : (
                            <EyeOnIconSvg className="h-5 w-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </InputContainer>
                  {!!jobId && (
                    <div className="mt-3 flex items-center">
                      <label
                        className={classNames('flex gap-2 text-sm', inter500.className)}>
                        <input
                          type="checkbox"
                          checked={isAssigningToJob}
                          onChange={(e) => setIsAssigningToJob(e.currentTarget.checked)}
                          className="checkbox-primary checkbox checkbox-sm rounded-md border-2 border-gray-300"
                        />

                        <span>Assign to this job</span>
                      </label>
                    </div>
                  )}

                  {isAssigningToJob && (
                    <InputContainer
                      label="Role"
                      error={formik.errors.role}
                      isTouched={formik.touched.role}
                      tooltipContent="Some info about">
                      <select
                        className="focus:input-primary active:input-primary select select-bordered w-full text-sm"
                        name="role"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.role}>
                        <option value="responder">Responder</option>
                        <option value="non-responder">Non-Responder</option>
                      </select>
                    </InputContainer>
                  )}
                  <button
                    className="btn btn-primary mt-5 mb-3 w-full"
                    type="submit"
                    disabled={!formik.isValid || isLoading}>
                    Create
                  </button>
                </div>
              </div>
            </ModalBody>
          </div>
        </form>
      </ModalContainer>
    </div>
  );
};

export default CreateNewAgentModal;
